import React, { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import './Reporting.css';

function Reporting() {
  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
      return process.env.REACT_APP_API_BASE_URL;
    }
    return '/api/v1';
  };

  const parseJsonResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      const text = await response.text();

      if (text.startsWith('Proxy error')) {
        throw new Error('Backend is not reachable. Start the API server on http://localhost:8080 and try again.');
      }

      if (text.toLowerCase().includes('<!doctype html>')) {
        throw new Error(
          'API returned HTML instead of JSON. Verify the backend is running on http://localhost:8080, JWT_SECRET is set for the server process, and REACT_APP_API_BASE_URL is not pointing to a frontend page.'
        );
      }

      throw new Error(`Expected JSON response from ${response.url} but received: ${text.slice(0, 80)}`);
    }

    return response.json();
  };

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [showOnlyWithOperations, setShowOnlyWithOperations] = useState(false);

  const formatDuration = (totalMs) => {
    const safeMs = Number.isFinite(totalMs) && totalMs > 0 ? totalMs : 0;
    const totalMinutes = Math.floor(safeMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      setCustomersError('');

      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          throw new Error('Missing auth token. Please log in again.');
        }

        const response = await fetch(`${getApiBaseUrl()}/customers`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await parseJsonResponse(response);

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load customers');
        }

        setCustomers(data.customers || []);
      } catch (error) {
        setCustomersError(error.message || 'Failed to load customers');
      } finally {
        setCustomersLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      return;
    }

    if (!dateRange.startDate || !dateRange.endDate) {
      setCustomersError('Please select both start and end date for reporting.');
      return;
    }

    setLoading(true);
    setCustomersError('');

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Missing auth token. Please log in again.');
      }

      const response = await fetch(`${getApiBaseUrl()}/customers/operations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: Number(selectedCustomer),
          start_date: `${dateRange.startDate}T00:00:00Z`,
          end_date: `${dateRange.endDate}T23:59:59Z`
        })
      });

      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate report');
      }

      const buckets = data.buckets || [];
      const bucketsForReport = showOnlyWithOperations
        ? buckets.filter((bucket) => (bucket.operations || []).length > 0)
        : buckets;

      const bucketStats = bucketsForReport.map((bucket) => {
        const operations = bucket.operations || [];
        const operationRows = operations.map((operation, index) => {
          const start = new Date(operation.operation_start);
          const end = new Date(operation.operation_end);
          const startMs = start.getTime();
          const endMs = end.getTime();
          const durationMs = !Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs >= startMs
            ? endMs - startMs
            : 0;

          return {
            key: `${bucket.id}-${operation.id || index}`,
            operationStart: Number.isNaN(startMs) ? '-' : start.toLocaleString(),
            operationEnd: Number.isNaN(endMs) ? '-' : end.toLocaleString(),
            operationDuration: formatDuration(durationMs),
            notes: operation.notes || '-'
          };
        });

        const totalDurationMs = operations.reduce((acc, operation) => {
          const start = new Date(operation.operation_start).getTime();
          const end = new Date(operation.operation_end).getTime();

          if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
            return acc;
          }

          return acc + (end - start);
        }, 0);

        return {
          bucketId: bucket.id,
          serialNumber: bucket.serial_number,
          bucketType: bucket.bucket_type,
          operationCount: operations.length,
          totalDurationMs,
          totalDuration: formatDuration(totalDurationMs),
          operationRows
        };
      });

      const totalOperations = bucketStats.reduce((acc, bucket) => acc + bucket.operationCount, 0);
      const totalOperationDurationMs = bucketStats.reduce((acc, bucket) => acc + bucket.totalDurationMs, 0);

      setReportData({
        customer: data.customer,
        generatedAt: new Date().toLocaleString(),
        filters: {
          showOnlyWithOperations
        },
        summary: {
          totalBuckets: bucketStats.length,
          totalOperations,
          totalOperationTime: formatDuration(totalOperationDurationMs)
        },
        operationStats: bucketStats
      });
    } catch (error) {
      setCustomersError(error.message || 'Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = () => {
    if (!reportData) return;
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report-${new Date().getTime()}.json`;
    link.click();
  };

  const handleExportPdf = () => {
    if (!reportData) return;

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 40;
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxWidth = doc.internal.pageSize.getWidth() - (margin * 2);
    const lineHeight = 16;
    let y = margin;

    const ensureSpace = (neededHeight = lineHeight) => {
      if (y + neededHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addText = (text, options = {}) => {
      const { size = 11, style = 'normal' } = options;
      doc.setFont('helvetica', style);
      doc.setFontSize(size);

      const lines = doc.splitTextToSize(String(text), maxWidth);
      ensureSpace(lines.length * lineHeight);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight;
    };

    addText(`${reportData.customer?.name || 'Customer'} Report`, { size: 16, style: 'bold' });
    addText(`Generated: ${reportData.generatedAt}`);
    if (reportData.filters?.showOnlyWithOperations) {
      addText('Filter: Showing only buckets with operations');
    }
    y += 8;

    addText('Summary', { size: 13, style: 'bold' });
    addText(`Total Buckets: ${reportData.summary.totalBuckets}`);
    addText(`Total Operations: ${reportData.summary.totalOperations}`);
    addText(`Total Operation Time: ${reportData.summary.totalOperationTime}`);
    y += 8;

    addText('Operations Statistics by Bucket', { size: 13, style: 'bold' });

    reportData.operationStats.forEach((stat) => {
      const bucketLabel = stat.serialNumber || `Bucket #${stat.bucketId}`;
      addText(`${bucketLabel} (${stat.bucketType || '-'})`, { style: 'bold' });
      addText(`Bucket totals: ${stat.operationCount} operations, ${stat.totalDuration}`);

      if (!stat.operationRows.length) {
        addText('No operations in selected date range');
        y += 4;
        return;
      }

      stat.operationRows.forEach((operation, index) => {
        addText(`Operation ${index + 1}`);
        addText(`Start: ${operation.operationStart}`);
        addText(`End: ${operation.operationEnd}`);
        addText(`Duration: ${operation.operationDuration}`);
        addText(`Notes: ${operation.notes || '-'}`);
        y += 4;
      });
    });

    doc.save(`report-${new Date().getTime()}.pdf`);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reporting</h1>
        <p>Generate and analyze reports on bucket operations and customer activity</p>
      </div>

      <div className="reporting-container">
        <div className="card report-controls">
          <h2>Generate Report</h2>
          {customersError && <div className="error-message">{customersError}</div>}
          <form onSubmit={handleGenerateReport} className="report-form">
            <div className="form-group">
              <label htmlFor="customer">Customer</label>
              <select
                id="customer"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                disabled={customersLoading}
              >
                <option value="">
                  {customersLoading ? 'Loading customers...' : 'Select customer'}
                </option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="showOnlyWithOperations">
                <input
                  id="showOnlyWithOperations"
                  type="checkbox"
                  checked={showOnlyWithOperations}
                  onChange={(e) => setShowOnlyWithOperations(e.target.checked)}
                />
                Show only buckets that have operations
              </label>
            </div>

            <button type="submit" disabled={loading || !selectedCustomer || customersLoading} className="btn btn-primary">
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </form>
        </div>

        {reportData && (
          <>
            <div className="card report-summary">
              <div className="report-header">
                <h2>{reportData.customer?.name || 'Customer'} Report</h2>
                <div className="report-actions">
                  <button onClick={handleExportReport} className="btn btn-secondary">
                    ↓ Export JSON
                  </button>
                  <button onClick={handleExportPdf} className="btn btn-secondary">
                    ↓ Export PDF
                  </button>
                </div>
              </div>
              <p className="report-timestamp">Generated: {reportData.generatedAt}</p>
              {reportData.filters?.showOnlyWithOperations && (
                <p className="report-filter-note">Filter: Showing only buckets with operations</p>
              )}

              <div className="summary-grid">
                <div className="summary-item">
                  <h4>Total Buckets</h4>
                  <p className="number">{reportData.summary.totalBuckets}</p>
                </div>
                <div className="summary-item">
                  <h4>Total Operations</h4>
                  <p className="number">{reportData.summary.totalOperations}</p>
                </div>
                <div className="summary-item">
                  <h4>Total Operation Time</h4>
                  <p className="number">{reportData.summary.totalOperationTime}</p>
                </div>
              </div>

              <div className="report-section">
                <h3>Operations Statistics by Bucket</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Type</th>
                      <th>Operation Start</th>
                      <th>Operation End</th>
                      <th>Operation Duration</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.operationStats.flatMap((stat) => {
                      const bucketLabel = stat.serialNumber || `Bucket #${stat.bucketId}`;
                      const rows = [
                        <tr key={`bucket-${stat.bucketId}`}>
                          <td><strong>{bucketLabel}</strong></td>
                          <td><strong>{stat.bucketType || '-'}</strong></td>
                          <td colSpan="4"><strong>Bucket totals: {stat.operationCount} operations, {stat.totalDuration}</strong></td>
                        </tr>
                      ];

                      if (stat.operationRows.length === 0) {
                        rows.push(
                          <tr key={`bucket-${stat.bucketId}-empty`}>
                            <td>{bucketLabel}</td>
                            <td>{stat.bucketType || '-'}</td>
                            <td colSpan="4">No operations in selected date range</td>
                          </tr>
                        );
                        return rows;
                      }

                      stat.operationRows.forEach((operation) => {
                        rows.push(
                          <tr key={operation.key}>
                            <td>{bucketLabel}</td>
                            <td>{stat.bucketType || '-'}</td>
                            <td>{operation.operationStart}</td>
                            <td>{operation.operationEnd}</td>
                            <td>{operation.operationDuration}</td>
                            <td>{operation.notes}</td>
                          </tr>
                        );
                      });

                      return rows;
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!reportData && (
          <div className="card empty-state-large">
            <p>Select a customer and generate a report to see data here</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reporting;
